/**
 * Llamadas HTTP del dominio del Optimizador de Cortes (cutlist).
 *
 * SIEMPRE sobre el cliente único `lib/api` (`docs/frontend_architecture.md`, Pr. 2):
 * ningún `fetch`/`ky` suelto. El cliente ya aporta `credentials: 'include'`, el
 * `X-CSRF-TOKEN` y el `X-Sede-Id` (sede activa) en cada request, así que el historial
 * se lista/guarda para la sede activa sin tocar la cabecera a mano (el backend resuelve
 * y valida la sede con `resolve_api_sede`).
 *
 * El servidor **NO ejecuta** el algoritmo: corre 100% en el navegador (`optimizer.ts`).
 * Estas llamadas solo **persisten y leen el historial** de jobs, igual que el SSR.
 * Réplica de la superficie de `application/api/domains/cutlist.py`:
 *
 *   GET  /api/v1/cutlist/jobs            -> historial (permiso `use_cutlist`)
 *   GET  /api/v1/cutlist/jobs/<id>       -> un job (permiso `use_cutlist`)
 *   POST /api/v1/cutlist/jobs            -> guardar un job (permiso `use_cutlist`)
 *
 * Un `ApiError` (401/403/400 por validación o sede/5xx) lo propaga el cliente y lo
 * gestionan los hooks/pantalla.
 */
import { api } from "@/lib/api";

import type {
  CutConfig,
  CutlistJobResponse,
  CutlistJobsList,
  OptimizeResult,
  Part,
  Stock,
} from "./types";

/** Cuerpo del guardado de un job (snapshot de entrada + resultado del navegador). */
export interface SaveCutlistJobBody {
  nombre: string | null;
  notas: string | null;
  parts: Part[];
  stocks: Stock[];
  config: CutConfig;
  result: OptimizeResult;
  /** Métricas resumen (el backend también las deriva de `result.stats`). */
  stats: OptimizeResult["stats"];
}

/**
 * Historial de optimizaciones de la sede activa (más recientes primero). `limit` por
 * defecto 50 (el backend lo acota a 200). Devuelve `{success, items}`.
 */
export async function fetchCutlistJobs(limit = 50): Promise<CutlistJobsList> {
  const response = await api.get("cutlist/jobs", {
    searchParams: { limit },
  });
  return (await response.json()) as CutlistJobsList;
}

/**
 * Recupera un job guardado por id (`GET /cutlist/jobs/<id>`). 404 si no existe o es de
 * otra sede (el backend no lo revela). Devuelve `{success, item}`.
 */
export async function fetchCutlistJob(jobId: number): Promise<CutlistJobResponse> {
  const response = await api.get(`cutlist/jobs/${jobId}`);
  return (await response.json()) as CutlistJobResponse;
}

/**
 * Guarda una optimización (`POST /cutlist/jobs`). El cuerpo va en JSON (no form). El
 * backend valida que haya piezas/tableros/resultado (400 si no) y persiste el snapshot
 * con el usuario y la sede activa. Devuelve `{success, item}` (201).
 */
export async function saveCutlistJob(
  body: SaveCutlistJobBody,
): Promise<CutlistJobResponse> {
  const response = await api.post("cutlist/jobs", { json: body });
  return (await response.json()) as CutlistJobResponse;
}
